/**
 * RISKEE Offline Queue Utilities
 * 
 * V1 SCOPE: Only "mark action done" is queued offline.
 * 
 * NON-GOALS for v1:
 * - No offline editing of existing risks
 * - No offline triage
 * - No attachments offline
 * - No user/role changes offline
 * 
 * CONFLICT STRATEGY: Server wins
 * - Each write has client_request_id (idempotency key)
 * - Server de-duplicates by action_id + status transition
 * - Client-side event log shows what was attempted
 * 
 * SECURITY:
 * - Authorization header is stored with queued request at enqueue time
 * - Token must be valid when flushing (expired tokens will fail)
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ACCESS_TOKEN_KEY = 'riskee_access_token';

/**
 * Get current auth token from storage
 */
const getAuthToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

/**
 * Queue an action status update for offline sync
 * Stores the full request including Authorization header at enqueue time
 * 
 * @param {string} actionId - The action ID
 * @param {string} newStatus - The new status (DONE, IN_PROGRESS, etc.)
 * @returns {Promise<{queued: boolean, pendingCount: number}>}
 */
export const queueActionStatusUpdate = async (actionId, newStatus) => {
  const registration = await navigator.serviceWorker?.ready;
  if (!registration?.active) {
    throw new Error('Service worker not available');
  }

  const token = getAuthToken();
  if (!token) {
    throw new Error('No auth token - cannot queue write');
  }

  const clientRequestId = crypto.randomUUID();
  
  const writeRequest = {
    client_request_id: clientRequestId,
    url: `${API_BASE}/api/actions/${actionId}`,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: { 
      status: newStatus,
      client_request_id: clientRequestId,
    },
    // Metadata for UI
    action: `Mark action ${actionId.slice(0, 8)} as ${newStatus}`,
    actionId,
    newStatus,
  };

  return new Promise((resolve, reject) => {
    const handler = (event) => {
      if (event.data?.type === 'WRITE_QUEUED') {
        navigator.serviceWorker.removeEventListener('message', handler);
        resolve({ 
          ...event.data, 
          client_request_id: clientRequestId 
        });
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handler);
    registration.active.postMessage({ type: 'QUEUE_WRITE', data: writeRequest });
    
    // Timeout fallback
    setTimeout(() => {
      navigator.serviceWorker.removeEventListener('message', handler);
      reject(new Error('Queue write timeout'));
    }, 5000);
  });
};

/**
 * Get pending writes from service worker
 * @returns {Promise<Array>}
 */
export const getPendingWrites = async () => {
  const registration = await navigator.serviceWorker?.ready;
  if (!registration?.active) return [];

  return new Promise((resolve) => {
    const handler = (event) => {
      if (event.data?.type === 'PENDING_WRITES') {
        navigator.serviceWorker.removeEventListener('message', handler);
        resolve(event.data.writes || []);
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handler);
    registration.active.postMessage({ type: 'GET_PENDING' });
    
    setTimeout(() => {
      navigator.serviceWorker.removeEventListener('message', handler);
      resolve([]);
    }, 2000);
  });
};

/**
 * Trigger manual sync
 * @returns {Promise<{lastSync: number, pending: number}>}
 */
export const triggerSync = async () => {
  const registration = await navigator.serviceWorker?.ready;
  if (!registration?.active) {
    throw new Error('Service worker not available');
  }

  return new Promise((resolve, reject) => {
    const handler = (event) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        navigator.serviceWorker.removeEventListener('message', handler);
        resolve(event.data);
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handler);
    registration.active.postMessage({ type: 'SYNC_NOW' });
    
    setTimeout(() => {
      navigator.serviceWorker.removeEventListener('message', handler);
      reject(new Error('Sync timeout'));
    }, 30000);
  });
};

/**
 * Check if an action has a pending write
 * @param {string} actionId 
 * @returns {Promise<boolean>}
 */
export const hasPendingWrite = async (actionId) => {
  const pending = await getPendingWrites();
  return pending.some(w => w.actionId === actionId);
};

/**
 * Check if we can operate offline (SW available + online status)
 */
export const getOfflineCapability = () => ({
  serviceWorkerAvailable: 'serviceWorker' in navigator,
  isOnline: navigator.onLine,
  canQueueWrites: 'serviceWorker' in navigator,
});

export default {
  queueActionStatusUpdate,
  getPendingWrites,
  triggerSync,
  hasPendingWrite,
  getOfflineCapability,
};
